import { useState, useEffect, useCallback } from 'react';
import {
  Link,
  Shield,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Download,
  Eye,
  X,
  Clock,
  Hash,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import clsx from 'clsx';
import type { AuditBlock, AuditChainStats } from '@/types';
import { blockchainAuditApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Colors ------------------------------------------------------------------

const EVENT_COLORS: Record<string, string> = {
  consolidation_run: '#3b82f6',
  journal_entry: '#8b5cf6',
  approval: '#10b981',
  period_close: '#f59e0b',
  data_import: '#ef4444',
  config_change: '#6366f1',
};

// --- Block Detail Modal ------------------------------------------------------

interface BlockDetailModalProps {
  block: AuditBlock;
  onClose: () => void;
}

function BlockDetailModal({ block, onClose }: BlockDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Block #{block.block_number}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Block Number</p>
              <p className="text-sm font-mono font-medium text-gray-900">#{block.block_number}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Event Type</p>
              <span className="badge text-xs" style={{
                backgroundColor: (EVENT_COLORS[block.event_type] || '#64748b') + '20',
                color: EVENT_COLORS[block.event_type] || '#64748b',
              }}>
                {block.event_type.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Entity Type</p>
              <p className="text-sm text-gray-700">{block.entity_type}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Timestamp</p>
              <p className="text-sm text-gray-700">{new Date(block.timestamp).toLocaleString()}</p>
            </div>
          </div>
          {block.user_email && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">User</p>
              <p className="text-sm text-gray-700">{block.user_email}</p>
            </div>
          )}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Current Hash</p>
            <p className="text-xs font-mono text-gray-700 break-all">{block.current_hash}</p>
          </div>
          {block.previous_hash && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Previous Hash</p>
              <p className="text-xs font-mono text-gray-700 break-all">{block.previous_hash}</p>
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Event Data</p>
            <pre className="text-xs font-mono text-gray-700 bg-gray-50 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap max-h-60">
              {JSON.stringify(block.data, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Page ---------------------------------------------------------------

export default function BlockchainAudit() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [blocks, setBlocks] = useState<AuditBlock[]>([]);
  const [totalBlocks, setTotalBlocks] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState<AuditChainStats | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<AuditBlock | null>(null);
  const [eventFilter, setEventFilter] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page, page_size: 20 };
      if (eventFilter) params.event_type = eventFilter;

      const [chainRes, statsRes] = await Promise.all([
        blockchainAuditApi.chain(params),
        blockchainAuditApi.stats(),
      ]);
      setBlocks(chainRes.items);
      setTotalBlocks(chainRes.total);
      setStats(statsRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load audit chain');
    } finally {
      setIsLoading(false);
    }
  }, [page, eventFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const result = await blockchainAuditApi.verify();
      if (result.is_valid) {
        addNotification({
          type: 'success',
          title: 'Chain Verified',
          message: `All ${result.blocks_verified} blocks verified successfully.`,
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Chain Verification Failed',
          message: `Chain integrity errors detected: ${result.errors.join(', ')}`,
        });
      }
      await loadData();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Verification Failed', message: apiErr.detail || 'Failed to verify chain.' });
    } finally {
      setVerifying(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await blockchainAuditApi.export({});
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit_chain_export.json';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Export Failed', message: apiErr.detail || 'Failed to export.' });
    }
  };

  // Build event type distribution chart
  const distroData = stats
    ? Object.entries(stats.event_type_distribution).map(([type, count]) => ({
        name: type.replace(/_/g, ' '),
        value: count,
        color: EVENT_COLORS[type] || '#94a3b8',
      }))
    : [];

  const uniqueEventTypes = stats
    ? Object.keys(stats.event_type_distribution).sort()
    : [];

  const totalPages = Math.ceil(totalBlocks / 20);

  if (isLoading && blocks.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Link className="w-5 h-5 text-orange-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="card p-6 animate-pulse">
          <div className="skeleton w-full h-40" />
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-12" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Link className="w-5 h-5 text-orange-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Blockchain Audit Trail</h1>
            <p className="text-sm text-gray-500 mt-0.5">Immutable, tamper-proof audit chain for all financial events</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="btn-secondary text-sm">
            <Download className="w-4 h-4 mr-1.5" />
            Export Chain
          </button>
          <button onClick={handleVerify} disabled={verifying} className="btn-primary text-sm">
            {verifying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Shield className="w-4 h-4 mr-1.5" />}
            Verify Chain
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Chain Integrity Banner */}
      {stats && (
        <div className={clsx(
          'card p-5 border-l-4',
          stats.is_verified ? 'border-l-green-500' : 'border-l-amber-500',
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {stats.is_verified ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-amber-600" />
              )}
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Chain Status: {stats.is_verified ? 'Verified' : 'Unverified'}
                </h3>
                <p className="text-xs text-gray-500">
                  Last verified: {stats.last_verified_at ? new Date(stats.last_verified_at).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-right">
              <div>
                <p className="text-xs text-gray-500">Total Blocks</p>
                <p className="text-lg font-bold text-gray-900 font-mono">{stats.total_blocks.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Date Range</p>
                <p className="text-xs text-gray-700">
                  {new Date(stats.first_block_timestamp).toLocaleDateString()} -{' '}
                  {new Date(stats.last_block_timestamp).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Event Distribution */}
        {distroData.length > 0 && (
          <div className="card p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Event Distribution</h2>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={distroData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={45}
                  dataKey="value"
                  paddingAngle={2}
                >
                  {distroData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Visual Hash Chain */}
        <div className="lg:col-span-2 card p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Chain Blocks</h2>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {blocks.slice(0, 8).map((block, i) => (
              <div key={block.id} className="flex items-center flex-shrink-0">
                <button
                  onClick={() => setSelectedBlock(block)}
                  className="flex flex-col items-center p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors min-w-[100px]"
                >
                  <Hash className="w-4 h-4 text-gray-400 mb-1" />
                  <span className="text-xs font-mono font-bold text-gray-700">#{block.block_number}</span>
                  <span className="text-[10px] text-gray-400 font-mono mt-0.5">
                    {block.current_hash.slice(0, 8)}...
                  </span>
                  <span className="badge text-[9px] mt-1" style={{
                    backgroundColor: (EVENT_COLORS[block.event_type] || '#64748b') + '20',
                    color: EVENT_COLORS[block.event_type] || '#64748b',
                  }}>
                    {block.event_type.replace(/_/g, ' ')}
                  </span>
                </button>
                {i < Math.min(blocks.length, 8) - 1 && (
                  <div className="w-6 h-px bg-gray-300 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Block Explorer */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-base font-semibold text-gray-900">Block Explorer</h2>
          <select
            className="select-field text-xs w-40"
            value={eventFilter}
            onChange={(e) => { setEventFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Event Types</option>
            {uniqueEventTypes.map((t) => (
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        {blocks.length === 0 ? (
          <div className="py-12 text-center">
            <Link className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No audit blocks found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="table-header w-20">Block #</th>
                    <th className="table-header w-32">Hash</th>
                    <th className="table-header">Event Type</th>
                    <th className="table-header">Entity Type</th>
                    <th className="table-header">Timestamp</th>
                    <th className="table-header w-20">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((block) => (
                    <tr key={block.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                      <td className="table-cell font-mono text-sm font-bold text-gray-700">#{block.block_number}</td>
                      <td className="table-cell">
                        <span className="font-mono text-xs text-gray-500" title={block.current_hash}>
                          {block.current_hash.slice(0, 12)}...
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className="badge text-xs" style={{
                          backgroundColor: (EVENT_COLORS[block.event_type] || '#64748b') + '20',
                          color: EVENT_COLORS[block.event_type] || '#64748b',
                        }}>
                          {block.event_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="table-cell text-sm text-gray-600">{block.entity_type}</td>
                      <td className="table-cell text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(block.timestamp).toLocaleString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <button
                          onClick={() => setSelectedBlock(block)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, totalBlocks)} of {totalBlocks} blocks
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-xs font-medium text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Block Detail Modal */}
      {selectedBlock && (
        <BlockDetailModal block={selectedBlock} onClose={() => setSelectedBlock(null)} />
      )}
    </div>
  );
}
